import * as React from "react";

const MOBILE_BREAKPOINT = 768;
const SIDEBAR_AUTO_COLLAPSE_BREAKPOINT = 1600;

function getInitialWidth(): number | null {
  if (typeof window !== "undefined") return window.innerWidth;
  return null;
}

/**
 * Mobile breakpoint detection. Initial render is always `false` so server HTML
 * matches the client hydration pass; `useEffect` then applies the real width.
 * (Reading `window` in the initial state breaks hydration: server `undefined`
 * vs client `true`/`false` — see AppLogo and other first-paint consumers.)
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkSize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    checkSize();

    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  return isMobile;
}

/**
 * Hook to detect if the screen is in "medium" size range where sidebar should auto-collapse.
 * Returns true when width is between mobile breakpoint and auto-collapse breakpoint.
 *
 * SSR-safe: initializes from window.innerWidth when available so the first
 * client render already reflects the correct collapsed state.
 */
export function useShouldAutoCollapse() {
  const [shouldAutoCollapse, setShouldAutoCollapse] = React.useState<boolean>(
    () => {
      const w = getInitialWidth();
      if (w == null) return false;
      return w >= MOBILE_BREAKPOINT && w < SIDEBAR_AUTO_COLLAPSE_BREAKPOINT;
    },
  );

  React.useEffect(() => {
    const checkSize = () => {
      const width = window.innerWidth;
      setShouldAutoCollapse(
        width >= MOBILE_BREAKPOINT && width < SIDEBAR_AUTO_COLLAPSE_BREAKPOINT,
      );
    };

    checkSize();

    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  return shouldAutoCollapse;
}

export { SIDEBAR_AUTO_COLLAPSE_BREAKPOINT };
